(() => {
  'use strict';

  /*
    MOISLAND Fly-to-Cart V1.1

    功能：
    - 加购成功后，商品图飞向浮动购物车
    - 第一次加购时显示浮动购物车按钮
    - 点击浮动购物车调用插件 window.openEAMiniCart()
    - 不自动打开购物车抽屉
    - 同步加购、减量、删除与清空后的购物车数量
    - 同时兼容 fetch、XMLHttpRequest 和插件购物车 DOM 更新
  */

  if (window.MoislandFlyCartInitialized) return;
  window.MoislandFlyCartInitialized = true;

  const CONFIG = {
    mobileBottomOffset: 178,
    desktopBottomOffset: 28,
    animationDuration: 720,
    cartRefreshDelay: 160,
    successDebounce: 320,
    passiveSyncInterval: 5000,
    cartInteractionDelays: [140, 520, 1250]
  };

  const state = {
    cartCount: 0,
    pendingSource: null,
    pendingPoint: null,
    lastSuccessAt: 0,
    refreshTimer: 0,
    passiveTimer: 0,
    interactionTimers: [],
    observerRefreshTimer: 0
  };

  const CART_MUTATION_PATTERN =
    /\/cart\/(add|change|update|clear)(?:\.js)?(?:[/?#]|$)/i;

  const CART_REGION_SELECTOR = [
    'cart-drawer',
    'cart-items',
    '[data-cart-drawer]',
    '[data-side-cart]',
    '[data-cart-items]',
    '[data-cart]',
    '[id*="cart" i]',
    '[class*="cart" i]'
  ].join(',');

  const getRequestUrl = (value) => {
    if (!value) return '';

    if (typeof value === 'string') {
      return value;
    }

    if (value instanceof URL) {
      return value.toString();
    }

    if (
      typeof Request !== 'undefined' &&
      value instanceof Request
    ) {
      return value.url;
    }

    return value.url || String(value);
  };

  const getCartMutationType = (value) => {
    const requestUrl = getRequestUrl(value);

    if (!requestUrl) return null;

    try {
      const url = new URL(
        requestUrl,
        window.location.origin
      );

      const match = `${url.pathname}${url.search}`.match(
        CART_MUTATION_PATTERN
      );

      return match?.[1]?.toLowerCase() || null;
    } catch (error) {
      const match = requestUrl.match(
        CART_MUTATION_PATTERN
      );

      return match?.[1]?.toLowerCase() || null;
    }
  };

  const isVisible = (element) => {
    if (!element) return false;

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) > 0 &&
      rect.width > 24 &&
      rect.height > 24 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  };

  const isAddToCartControl = (element) => {
    if (!element || element.disabled) return false;

    const form = element.closest('form');
    const formAction =
      form?.getAttribute('action') ||
      form?.action ||
      '';

    if (getCartMutationType(formAction) === 'add') return true;

    if (
      element.matches(
        '[name="add"], [data-add-to-cart], .product-form__submit'
      )
    ) {
      return true;
    }

    const text = String(
      element.textContent ||
      element.value ||
      element.getAttribute('aria-label') ||
      ''
    )
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    return (
      text.includes('add to cart') ||
      text.includes('add to bag') ||
      text.includes('加入购物车') ||
      text.includes('添加到购物车') ||
      text === '加购'
    );
  };

  const findProductImage = (control) => {
    const preferredSelectors = [
      '.product__media-item.is-active img',
      '.product__media-item[aria-hidden="false"] img',
      '.product-media-container img',
      '.product__media img',
      '[data-media-id] img',
      '.product img'
    ];

    const scopes = [
      control?.closest(
        '[id^="MainProduct-"], .product, .shopify-section'
      ),
      document
    ].filter(Boolean);

    for (const scope of scopes) {
      for (const selector of preferredSelectors) {
        const candidates = Array.from(
          scope.querySelectorAll(selector)
        ).filter(isVisible);

        if (!candidates.length) continue;

        candidates.sort((a, b) => {
          const rectA = a.getBoundingClientRect();
          const rectB = b.getBoundingClientRect();

          return (
            rectB.width * rectB.height -
            rectA.width * rectA.height
          );
        });

        return candidates[0];
      }
    }

    return null;
  };

  const capturePendingSource = (control, event) => {
    const image = findProductImage(control);

    if (image) {
      const rect = image.getBoundingClientRect();

      state.pendingSource = {
        src:
          image.currentSrc ||
          image.src ||
          image.getAttribute('src'),
        rect: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        }
      };
    } else {
      state.pendingSource = null;
    }

    state.pendingPoint = {
      x:
        event?.clientX ||
        control?.getBoundingClientRect().left ||
        window.innerWidth / 2,
      y:
        event?.clientY ||
        control?.getBoundingClientRect().top ||
        window.innerHeight / 2
    };
  };

  const injectStyles = () => {
    if (document.getElementById('moi-fly-cart-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'moi-fly-cart-styles';
    style.textContent = `
      :root {
        --moi-fly-cart-mobile-bottom:
          ${CONFIG.mobileBottomOffset}px;
        --moi-fly-cart-desktop-bottom:
          ${CONFIG.desktopBottomOffset}px;
      }

      #moi-fly-cart-button {
        position: fixed;
        right: 1.4rem;
        bottom:
          calc(
            var(--moi-fly-cart-mobile-bottom) +
            env(safe-area-inset-bottom, 0px)
          );
        z-index: 2147482000;
        display: inline-flex;
        width: 5.6rem;
        height: 5.6rem;
        padding: 0;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 999px;
        background: #0b3d2e;
        color: #fff;
        cursor: pointer;
        opacity: 0;
        pointer-events: none;
        transform:
          translateY(1rem)
          scale(0.68);
        box-shadow:
          0 1.1rem 2.8rem rgba(11, 61, 46, 0.28);
        transition:
          opacity 220ms ease,
          transform 320ms cubic-bezier(.2,.9,.3,1.25),
          box-shadow 220ms ease;
        -webkit-tap-highlight-color: transparent;
      }

      #moi-fly-cart-button.is-visible {
        opacity: 1;
        pointer-events: auto;
        transform:
          translateY(0)
          scale(1);
      }

      #moi-fly-cart-button:hover {
        transform:
          translateY(-0.15rem)
          scale(1.035);
        box-shadow:
          0 1.3rem 3rem rgba(11, 61, 46, 0.34);
      }

      #moi-fly-cart-button:focus-visible {
        outline: 0.25rem solid #fff;
        outline-offset: 0.25rem;
        box-shadow:
          0 0 0 0.5rem #0b3d2e,
          0 1.3rem 3rem rgba(11, 61, 46, 0.34);
      }

      #moi-fly-cart-button svg {
        width: 2.65rem;
        height: 2.65rem;
      }

      #moi-fly-cart-count {
        position: absolute;
        top: -0.25rem;
        right: -0.15rem;
        display: inline-flex;
        min-width: 2.15rem;
        height: 2.15rem;
        padding: 0 0.5rem;
        align-items: center;
        justify-content: center;
        border: 0.2rem solid #fff;
        border-radius: 999px;
        background: #f5ef61;
        color: #17251f;
        font-size: 1.15rem;
        line-height: 1;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
        box-sizing: border-box;
      }

      #moi-fly-cart-button.is-pulsing {
        animation:
          moi-cart-button-pulse
          520ms
          cubic-bezier(.2,.85,.3,1.35);
      }

      #moi-fly-cart-count.is-bumping {
        animation:
          moi-cart-count-bump
          460ms
          cubic-bezier(.2,.9,.3,1.35);
      }

      .moi-fly-cart-product {
        position: fixed;
        z-index: 2147481999;
        margin: 0;
        object-fit: cover;
        pointer-events: none;
        will-change:
          transform,
          opacity,
          border-radius;
        box-shadow:
          0 0.8rem 2.4rem rgba(20, 25, 22, 0.22);
      }

      .moi-fly-cart-dot {
        position: fixed;
        z-index: 2147481999;
        width: 2.4rem;
        height: 2.4rem;
        border-radius: 999px;
        background: #f5ef61;
        pointer-events: none;
        will-change:
          transform,
          opacity;
        box-shadow:
          0 0.6rem 1.8rem rgba(20, 25, 22, 0.22);
      }

      @keyframes moi-cart-button-pulse {
        0% {
          transform: scale(1);
        }
        42% {
          transform: scale(1.18) rotate(-5deg);
        }
        72% {
          transform: scale(0.94) rotate(2deg);
        }
        100% {
          transform: scale(1);
        }
      }

      @keyframes moi-cart-count-bump {
        0% {
          transform: scale(1);
        }
        45% {
          transform: scale(1.35);
        }
        100% {
          transform: scale(1);
        }
      }

      @media screen and (min-width: 750px) {
        #moi-fly-cart-button {
          right: 2.6rem;
          bottom:
            calc(
              var(--moi-fly-cart-desktop-bottom) +
              env(safe-area-inset-bottom, 0px)
            );
          width: 6rem;
          height: 6rem;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        #moi-fly-cart-button,
        #moi-fly-cart-count {
          transition: none;
          animation: none !important;
        }

        .moi-fly-cart-product,
        .moi-fly-cart-dot {
          display: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  };

  const createButton = () => {
    let button = document.getElementById(
      'moi-fly-cart-button'
    );

    if (button) return button;

    button = document.createElement('button');
    button.id = 'moi-fly-cart-button';
    button.type = 'button';
    button.setAttribute(
      'aria-label',
      'Open shopping cart'
    );

    button.innerHTML = `
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 8H19L18 20H6L5 8Z"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />
        <path
          d="M9 9V6.5C9 4.6 10.3 3 12 3C13.7 3 15 4.6 15 6.5V9"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
      </svg>
      <span
        id="moi-fly-cart-count"
        aria-hidden="true"
      >0</span>
    `;

    button.addEventListener('click', () => {
      if (
        typeof window.openEAMiniCart ===
        'function'
      ) {
        window.openEAMiniCart();
        return;
      }

      const themeCartControl =
        document.querySelector(
          [
            '[data-cart-icon-bubble]',
            '#cart-icon-bubble',
            'a[href="/cart"]',
            'a[href$="/cart"]'
          ].join(',')
        );

      if (themeCartControl) {
        themeCartControl.click();
        return;
      }

      window.location.assign(
        `${window.Shopify?.routes?.root || '/'}cart`
      );
    });

    document.body.appendChild(button);
    return button;
  };

  const setCartCount = (count, {
    animate = false
  } = {}) => {
    const button = createButton();
    const badge = button.querySelector(
      '#moi-fly-cart-count'
    );

    const safeCount = Math.max(
      0,
      Math.round(Number(count) || 0)
    );

    state.cartCount = safeCount;
    badge.textContent =
      safeCount > 99 ? '99+' : String(safeCount);

    button.classList.toggle(
      'is-visible',
      safeCount > 0
    );

    if (animate && safeCount > 0) {
      button.classList.remove('is-pulsing');
      badge.classList.remove('is-bumping');

      void button.offsetWidth;

      button.classList.add('is-pulsing');
      badge.classList.add('is-bumping');

      window.setTimeout(() => {
        button.classList.remove('is-pulsing');
        badge.classList.remove('is-bumping');
      }, 560);
    }
  };

  const refreshCartCount = ({
    animate = false,
    delay = CONFIG.cartRefreshDelay
  } = {}) => {
    window.clearTimeout(state.refreshTimer);

    state.refreshTimer = window.setTimeout(
      async () => {
        try {
          const root =
            window.Shopify?.routes?.root || '/';

          const response = await fetch(
            `${root}cart.js`,
            {
              credentials: 'same-origin',
              cache: 'no-store',
              headers: {
                Accept: 'application/json'
              }
            }
          );

          if (!response.ok) return;

          const cart = await response.json();

          setCartCount(
            cart.item_count,
            { animate }
          );
        } catch (error) {
          // Cart count failure must not interrupt checkout.
        }
      },
      Math.max(0, Number(delay) || 0)
    );
  };

  const scheduleCartRefreshes = ({
    animateFirst = false,
    delays = CONFIG.cartInteractionDelays
  } = {}) => {
    state.interactionTimers.forEach(
      (timer) => window.clearTimeout(timer)
    );

    state.interactionTimers = delays.map(
      (delay, index) => window.setTimeout(
        () => refreshCartCount({
          animate: animateFirst && index === 0,
          delay: 0
        }),
        delay
      )
    );
  };

  const animateToCart = () => {
    if (
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      return;
    }

    const button = createButton();

    button.classList.add('is-visible');

    const targetRect =
      button.getBoundingClientRect();

    const targetX =
      targetRect.left + targetRect.width / 2;

    const targetY =
      targetRect.top + targetRect.height / 2;

    const source = state.pendingSource;

    if (source?.src && source?.rect) {
      const startRect = source.rect;
      const maxStartSize = 92;
      const startWidth = Math.max(
        44,
        Math.min(startRect.width, maxStartSize)
      );

      const ratio =
        startRect.height / Math.max(1, startRect.width);

      const startHeight = Math.max(
        44,
        Math.min(
          startRect.height,
          startWidth * ratio
        )
      );

      const startX =
        startRect.left +
        startRect.width / 2 -
        startWidth / 2;

      const startY =
        startRect.top +
        startRect.height / 2 -
        startHeight / 2;

      const image = document.createElement('img');

      image.className = 'moi-fly-cart-product';
      image.src = source.src;
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');

      Object.assign(image.style, {
        left: `${startX}px`,
        top: `${startY}px`,
        width: `${startWidth}px`,
        height: `${startHeight}px`,
        borderRadius: '1.4rem'
      });

      document.body.appendChild(image);

      const deltaX =
        targetX - (startX + startWidth / 2);

      const deltaY =
        targetY - (startY + startHeight / 2);

      const animation = image.animate(
        [
          {
            transform:
              'translate3d(0, 0, 0) scale(1)',
            opacity: 1,
            borderRadius: '1.4rem'
          },
          {
            transform:
              `translate3d(${deltaX * 0.52}px, ${deltaY * 0.22 - 44}px, 0) scale(0.74)`,
            opacity: 0.95,
            offset: 0.42
          },
          {
            transform:
              `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.12)`,
            opacity: 0.18,
            borderRadius: '999px'
          }
        ],
        {
          duration: CONFIG.animationDuration,
          easing:
            'cubic-bezier(.3,.02,.2,1)',
          fill: 'forwards'
        }
      );

      animation.finished
        .catch(() => {})
        .finally(() => image.remove());

      return;
    }

    const point = state.pendingPoint || {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    };

    const dot = document.createElement('span');
    dot.className = 'moi-fly-cart-dot';

    Object.assign(dot.style, {
      left: `${point.x - 12}px`,
      top: `${point.y - 12}px`
    });

    document.body.appendChild(dot);

    const deltaX = targetX - point.x;
    const deltaY = targetY - point.y;

    const animation = dot.animate(
      [
        {
          transform:
            'translate3d(0, 0, 0) scale(1)',
          opacity: 1
        },
        {
          transform:
            `translate3d(${deltaX * 0.52}px, ${deltaY * 0.22 - 32}px, 0) scale(0.78)`,
          opacity: 0.9,
          offset: 0.45
        },
        {
          transform:
            `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.1)`,
          opacity: 0.1
        }
      ],
      {
        duration: CONFIG.animationDuration,
        easing:
          'cubic-bezier(.3,.02,.2,1)',
        fill: 'forwards'
      }
    );

    animation.finished
      .catch(() => {})
      .finally(() => dot.remove());
  };

  const handleSuccessfulAdd = () => {
    const now = Date.now();

    if (
      now - state.lastSuccessAt <
      CONFIG.successDebounce
    ) {
      return;
    }

    state.lastSuccessAt = now;

    animateToCart();

    scheduleCartRefreshes({
      animateFirst: true,
      delays: [110, 520, 1250]
    });
  };

  const handleSuccessfulCartMutation = (
    mutationType
  ) => {
    if (mutationType === 'add') {
      handleSuccessfulAdd();
      return;
    }

    scheduleCartRefreshes({
      animateFirst: false,
      delays: [90, 420, 1100]
    });
  };

  const patchFetch = () => {
    if (window.fetch.__moiFlyCartPatched) return;

    const originalFetch = window.fetch;

    const patchedFetch = async function(
      input,
      init
    ) {
      const mutationType =
        getCartMutationType(input);

      const response = await originalFetch.apply(
        this,
        arguments
      );

      if (mutationType && response.ok) {
        window.setTimeout(
          () => handleSuccessfulCartMutation(
            mutationType
          ),
          0
        );
      }

      return response;
    };

    patchedFetch.__moiFlyCartPatched = true;
    patchedFetch.__moiOriginalFetch =
      originalFetch;

    window.fetch = patchedFetch;
  };

  const patchXhr = () => {
    if (
      XMLHttpRequest.prototype
        .__moiFlyCartPatched
    ) {
      return;
    }

    const originalOpen =
      XMLHttpRequest.prototype.open;

    const originalSend =
      XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open =
      function(method, url) {
        this.__moiCartMutationType =
          getCartMutationType(url);

        return originalOpen.apply(
          this,
          arguments
        );
      };

    XMLHttpRequest.prototype.send =
      function() {
        if (this.__moiCartMutationType) {
          this.addEventListener(
            'load',
            () => {
              if (
                this.status >= 200 &&
                this.status < 300
              ) {
                handleSuccessfulCartMutation(
                  this.__moiCartMutationType
                );
              }
            },
            { once: true }
          );
        }

        return originalSend.apply(
          this,
          arguments
        );
      };

    XMLHttpRequest.prototype
      .__moiFlyCartPatched = true;
  };

  const bindCapture = () => {
    const capture = (event) => {
      const control =
        event.target.closest(
          'button, input[type="submit"], a'
        );

      if (!isAddToCartControl(control)) {
        return;
      }

      capturePendingSource(
        control,
        event
      );
    };

    document.addEventListener(
      'pointerdown',
      capture,
      true
    );

    document.addEventListener(
      'click',
      capture,
      true
    );
  };

  const isInsideCartUi = (element) => {
    if (!element?.closest) return false;

    if (element.closest('#moi-fly-cart-button')) {
      return false;
    }

    return Boolean(
      element.closest(CART_REGION_SELECTOR)
    );
  };

  const bindCartInteractionSync = () => {
    const scheduleFromInteraction = (event) => {
      const control = event.target.closest?.(
        [
          'button',
          'a',
          'input',
          'select',
          '[role="button"]'
        ].join(',')
      );

      if (!control || !isInsideCartUi(control)) {
        return;
      }

      const label = String(
        control.getAttribute('aria-label') ||
        control.getAttribute('title') ||
        control.textContent ||
        control.value ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const looksLikeCartChange =
        control.matches(
          [
            'input[type="number"]',
            '[name="updates[]"]',
            '[data-quantity-input]',
            '[data-cart-remove]',
            '[data-remove]',
            '[data-quantity-plus]',
            '[data-quantity-minus]'
          ].join(',')
        ) ||
        [
          'remove',
          'delete',
          'trash',
          'clear',
          'increase',
          'decrease',
          'plus',
          'minus',
          '删除',
          '移除',
          '清空',
          '增加',
          '减少',
          '+',
          '−',
          '-'
        ].some((word) => label === word || label.includes(word));

      if (looksLikeCartChange) {
        scheduleCartRefreshes({
          delays: [160, 600, 1450]
        });
      }
    };

    document.addEventListener(
      'click',
      scheduleFromInteraction,
      true
    );

    document.addEventListener(
      'change',
      scheduleFromInteraction,
      true
    );
  };

  const observeCartDom = () => {
    if (!document.body) return;

    const observer = new MutationObserver(
      (mutations) => {
        const cartChanged = mutations.some(
          (mutation) => {
            const target =
              mutation.target.nodeType === 1
                ? mutation.target
                : mutation.target.parentElement;

            if (
              !target ||
              target.closest?.(
                '#moi-fly-cart-button'
              )
            ) {
              return false;
            }

            if (isInsideCartUi(target)) {
              return true;
            }

            return Array.from(
              mutation.addedNodes
            )
              .concat(
                Array.from(mutation.removedNodes)
              )
              .some((node) => (
                node.nodeType === 1 &&
                !node.closest?.(
                  '#moi-fly-cart-button'
                ) &&
                (
                  node.matches?.(
                    CART_REGION_SELECTOR
                  ) ||
                  node.querySelector?.(
                    CART_REGION_SELECTOR
                  )
                )
              ));
          }
        );

        if (!cartChanged) return;

        window.clearTimeout(
          state.observerRefreshTimer
        );

        state.observerRefreshTimer =
          window.setTimeout(
            () => refreshCartCount({
              delay: 0
            }),
            260
          );
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  };

  const bindCartEvents = () => {
    [
      'cart:updated',
      'cart:update',
      'cart:refresh',
      'cart-drawer:updated',
      'cart-drawer:refresh',
      'cart-updated',
      'cartUpdate',
      'product:added',
      'ajaxProduct:added'
    ].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => scheduleCartRefreshes({
          delays: [80, 420, 1100]
        })
      );
    });
  };

  const startPassiveSync = () => {
    window.clearInterval(
      state.passiveTimer
    );

    state.passiveTimer = window.setInterval(
      () => {
        if (
          document.visibilityState ===
          'visible'
        ) {
          refreshCartCount({ delay: 0 });
        }
      },
      CONFIG.passiveSyncInterval
    );
  };

  const start = () => {
    injectStyles();
    createButton();
    bindCapture();
    bindCartInteractionSync();
    bindCartEvents();
    patchFetch();
    patchXhr();
    observeCartDom();
    startPassiveSync();
    refreshCartCount({ delay: 0 });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    () => {
      injectStyles();
      createButton();
      refreshCartCount();
    }
  );

  window.addEventListener(
    'pageshow',
    () => refreshCartCount({
      delay: 0
    })
  );

  window.addEventListener(
    'focus',
    () => refreshCartCount({
      delay: 0
    })
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        refreshCartCount({
          delay: 0
        });
      }
    }
  );
})();
